import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import {
  Trophy, Crown, Medal, Globe, User, Heart, MessageSquare, BookOpen,
  Eye, ShoppingBag, Lightbulb, Compass, ShieldCheck, ChevronDown
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const BADGE_ICONS = {
  explorer: Compass,
  lore_keeper: BookOpen,
  social_butterfly: MessageSquare,
  collector: Heart,
  patron: ShoppingBag,
  theorist: Lightbulb,
  champion: Trophy,
  trusted: ShieldCheck,
  "shield-check": ShieldCheck,
  "book-open": BookOpen,
  "message-circle": MessageSquare,
  heart: Heart,
  "shopping-bag": ShoppingBag,
  lightbulb: Lightbulb,
  trophy: Trophy,
  compass: Compass,
};

const RANK_STYLES = [
  { bg: "bg-amber-500/15", border: "border-amber-500/30", icon: Crown, color: "text-amber-400" },
  { bg: "bg-slate-300/10", border: "border-slate-300/20", icon: Medal, color: "text-slate-300" },
  { bg: "bg-amber-700/10", border: "border-amber-700/20", icon: Medal, color: "text-amber-700" },
];

const LeaderboardPage = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState("universes");
  const [universeRanking, setUniverseRanking] = useState([]);
  const [fanRanking, setFanRanking] = useState([]);
  const [badgeDefs, setBadgeDefs] = useState([]);
  const [universes, setUniverses] = useState([]);
  const [selectedUniverse, setSelectedUniverse] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    const fetchData = async () => {
      setLoading(true);
      try {
        const [uniRank, fans, badges, unis] = await Promise.all([
          axios.get(`${API}/leaderboard/universes`),
          axios.get(`${API}/leaderboard/fans`),
          axios.get(`${API}/leaderboard/badges`),
          axios.get(`${API}/universes`),
        ]);
        setUniverseRanking(uniRank.data);
        setFanRanking(fans.data);
        setBadgeDefs(badges.data);
        setUniverses(unis.data);
      } catch {
        toast.error("Failed to load leaderboard");
      }
      setLoading(false);
    };
    fetchData();
  }, [user]);

  const fetchFansByUniverse = async (universeId) => {
    setSelectedUniverse(universeId);
    try {
      const url = universeId && universeId !== "all"
        ? `${API}/leaderboard/fans?universe_id=${universeId}`
        : `${API}/leaderboard/fans`;
      const res = await axios.get(url);
      setFanRanking(res.data);
    } catch { toast.error("Failed to load fans"); }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex flex-col bg-[var(--bg-base)]">
        <Navbar />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center card-surface p-12">
            <Trophy className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white font-['Outfit'] mb-2">Sign in to view Leaderboard</h2>
            <p className="text-slate-400 text-sm mb-4">Rankings are available to logged-in fans</p>
            <Link to="/login"><Button className="btn-primary">Sign In</Button></Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-[var(--bg-base)]">
      <SEOHead title="Leaderboard" description="FanVerse rankings — top universes and fans." />
      <Navbar />
      <main className="flex-1 pt-20 pb-16 px-4 max-w-5xl mx-auto w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-amber-500/20 bg-amber-500/5 mb-3">
            <Trophy className="w-3.5 h-3.5 text-amber-500" />
            <span className="text-xs font-mono tracking-wider text-amber-500 uppercase">Leaderboard</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-white font-['Outfit'] mb-2">FanVerse Rankings</h1>
          <p className="text-slate-400 text-sm">See who's leading the fandom</p>
        </div>

        {/* Tabs */}
        <div className="flex justify-center gap-2 mb-8">
          <button
            onClick={() => setTab("universes")}
            className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all flex items-center gap-2 ${
              tab === "universes" ? "bg-amber-500/20 text-amber-400 border border-amber-500/30" : "bg-white/5 text-slate-400 border border-white/10 hover:text-white"
            }`}
            data-testid="tab-universes"
          >
            <Globe className="w-4 h-4" /> Universe Rankings
          </button>
          <button
            onClick={() => setTab("fans")}
            className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all flex items-center gap-2 ${
              tab === "fans" ? "bg-purple-500/20 text-purple-400 border border-purple-500/30" : "bg-white/5 text-slate-400 border border-white/10 hover:text-white"
            }`}
            data-testid="tab-fans"
          >
            <User className="w-4 h-4" /> Fan Rankings
          </button>
          <button
            onClick={() => setTab("badges")}
            className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all flex items-center gap-2 ${
              tab === "badges" ? "bg-pink-500/20 text-pink-400 border border-pink-500/30" : "bg-white/5 text-slate-400 border border-white/10 hover:text-white"
            }`}
            data-testid="tab-badges"
          >
            <Medal className="w-4 h-4" /> Badges
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20"><div className="spinner"></div></div>
        ) : (
          <>
            {/* Universe Rankings */}
            {tab === "universes" && (
              <div className="space-y-3" data-testid="universe-rankings">
                {universeRanking.map((u, i) => {
                  const style = RANK_STYLES[i] || { bg: "bg-white/[0.03]", border: "border-white/5", icon: null, color: "text-slate-500" };
                  const RankIcon = style.icon;
                  return (
                    <Link
                      key={u.id}
                      to={`/universe/${u.slug}`}
                      className={`flex items-center gap-4 p-4 rounded-xl border ${style.border} ${style.bg} hover:border-amber-500/30 transition-all group`}
                      data-testid={`universe-rank-${u.rank}`}
                    >
                      {/* Rank */}
                      <div className="flex-shrink-0 w-10 text-center">
                        {RankIcon ? (
                          <RankIcon className={`w-6 h-6 ${style.color} mx-auto`} />
                        ) : (
                          <span className="text-lg font-bold text-slate-500 font-mono">#{u.rank}</span>
                        )}
                      </div>

                      {/* Image */}
                      {(u.cover_image || u.images?.[0]) ? (
                        <img src={u.cover_image || u.images[0]} alt={u.name} className="w-12 h-12 rounded-lg object-cover border border-white/10 flex-shrink-0" />
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                          <Globe className="w-5 h-5 text-amber-500" />
                        </div>
                      )}

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <h3 className="text-white font-semibold font-['Outfit'] group-hover:text-amber-400 transition-colors">{u.name}</h3>
                        <div className="flex items-center gap-3 text-[10px] text-slate-500 mt-0.5">
                          <span className="capitalize">{u.category}</span>
                        </div>
                      </div>

                      {/* Stats */}
                      <div className="flex items-center gap-4 text-xs flex-shrink-0">
                        <div className="text-center">
                          <Eye className="w-3.5 h-3.5 text-slate-500 mx-auto mb-0.5" />
                          <span className="text-slate-400">{u.views}</span>
                        </div>
                        <div className="text-center">
                          <Heart className="w-3.5 h-3.5 text-red-400 mx-auto mb-0.5" />
                          <span className="text-slate-400">{u.favorites}</span>
                        </div>
                        <div className="text-center">
                          <MessageSquare className="w-3.5 h-3.5 text-purple-400 mx-auto mb-0.5" />
                          <span className="text-slate-400">{u.comments}</span>
                        </div>
                        <div className="text-center">
                          <User className="w-3.5 h-3.5 text-cyan-400 mx-auto mb-0.5" />
                          <span className="text-slate-400">{u.characters}</span>
                        </div>
                        <div className="text-right min-w-[50px]">
                          <span className="text-amber-500 font-mono font-bold text-sm">{u.score}</span>
                          <div className="text-[9px] text-slate-600">pts</div>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}

            {/* Fan Rankings */}
            {tab === "fans" && (
              <div>
                {/* Universe filter */}
                <div className="flex justify-end mb-4">
                  <Select value={selectedUniverse || "all"} onValueChange={fetchFansByUniverse}>
                    <SelectTrigger className="w-48 bg-[var(--bg-elevated)] border-white/10 text-white text-xs" data-testid="fan-universe-filter">
                      <SelectValue placeholder="All Universes" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#111827] border-white/10">
                      <SelectItem value="all">All Universes</SelectItem>
                      {universes.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                {fanRanking.length === 0 ? (
                  <div className="text-center py-16 card-surface">
                    <User className="w-10 h-10 text-slate-600 mx-auto mb-3" />
                    <p className="text-slate-400">No active fans yet. Start contributing to appear here!</p>
                  </div>
                ) : (
                  <div className="space-y-3" data-testid="fan-rankings">
                    {fanRanking.map((f, i) => {
                      const style = RANK_STYLES[i] || { bg: "bg-white/[0.03]", border: "border-white/5", icon: null, color: "text-slate-500" };
                      const RankIcon = style.icon;
                      return (
                        <div
                          key={f.id}
                          className={`flex items-center gap-4 p-4 rounded-xl border ${style.border} ${style.bg} transition-all`}
                          data-testid={`fan-rank-${f.rank}`}
                        >
                          {/* Rank */}
                          <div className="flex-shrink-0 w-10 text-center">
                            {RankIcon ? (
                              <RankIcon className={`w-6 h-6 ${style.color} mx-auto`} />
                            ) : (
                              <span className="text-lg font-bold text-slate-500 font-mono">#{f.rank}</span>
                            )}
                          </div>

                          {/* Avatar */}
                          {f.avatar ? (
                            <img src={f.avatar} alt={f.name} className="w-10 h-10 rounded-full object-cover border border-white/10" />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-purple-500/15 flex items-center justify-center">
                              <User className="w-5 h-5 text-purple-400" />
                            </div>
                          )}

                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h3 className="text-white font-semibold font-['Outfit']">{f.name}</h3>
                              {f.role === "admin" && (
                                <span className="text-[9px] font-mono bg-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded">ADMIN</span>
                              )}
                            </div>
                            {/* Badges */}
                            {f.badges.length > 0 && (
                              <div className="flex gap-1 mt-1">
                                {f.badges.map(bid => {
                                  const bdef = badgeDefs.find(b => b.id === bid);
                                  if (!bdef) return null;
                                  const BIcon = BADGE_ICONS[bdef.icon] || BADGE_ICONS[bid] || Medal;
                                  return (
                                    <span
                                      key={bid}
                                      title={`${bdef.name}: ${bdef.description}`}
                                      className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-mono"
                                      style={{ backgroundColor: `${bdef.color}15`, color: bdef.color, border: `1px solid ${bdef.color}30` }}
                                      data-testid={`badge-${bid}`}
                                    >
                                      <BIcon className="w-2.5 h-2.5" /> {bdef.name}
                                    </span>
                                  );
                                })}
                              </div>
                            )}
                          </div>

                          {/* Stats */}
                          <div className="flex items-center gap-3 text-xs flex-shrink-0">
                            <div className="text-center" title="Contributions">
                              <BookOpen className="w-3.5 h-3.5 text-amber-400 mx-auto mb-0.5" />
                              <span className="text-slate-400">{f.contributions}</span>
                            </div>
                            <div className="text-center" title="Comments">
                              <MessageSquare className="w-3.5 h-3.5 text-purple-400 mx-auto mb-0.5" />
                              <span className="text-slate-400">{f.comments}</span>
                            </div>
                            <div className="text-center" title="Theories">
                              <Lightbulb className="w-3.5 h-3.5 text-pink-400 mx-auto mb-0.5" />
                              <span className="text-slate-400">{f.theories}</span>
                            </div>
                            <div className="text-right min-w-[50px]">
                              <span className="text-purple-400 font-mono font-bold text-sm">{f.score}</span>
                              <div className="text-[9px] text-slate-600">pts</div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Badges Tab */}
            {tab === "badges" && (
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="badges-grid">
                {badgeDefs.map(b => {
                  const BIcon = BADGE_ICONS[b.icon] || BADGE_ICONS[b.id] || Medal;
                  return (
                    <div key={b.id} className="card-surface p-5 text-center" data-testid={`badge-def-${b.id}`}>
                      <div
                        className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3"
                        style={{ backgroundColor: `${b.color}15`, border: `2px solid ${b.color}40` }}
                      >
                        <BIcon className="w-6 h-6" style={{ color: b.color }} />
                      </div>
                      <h3 className="text-white font-semibold font-['Outfit'] text-sm mb-1">{b.name}</h3>
                      <p className="text-slate-500 text-xs">{b.description}</p>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default LeaderboardPage;
